import React, { useState } from 'react';
import { Box, Button, Collapse, Paper, Typography } from '@mui/material';
import { LockOutlined } from '@mui/icons-material';
import { AuthForm } from './AuthForm';
import { authService } from '../services/authService';
import { preferencesManager } from '../utils/preferencesManager';

interface SignInNudgeProps {
  /** What signing in unlocks, e.g. "log your dives" — completes "Sign in to …" */
  feature: string;
  /** Optional supporting copy shown under the headline */
  description?: string;
  /** Called after a successful sign-in or registration */
  onSignedIn: () => void;
}

/**
 * Friendly inline prompt for signed-out visitors on account-only features.
 * Expands to the full sign-in / register form in place so users don't lose
 * the page they were on.
 */
const SignInNudge: React.FC<SignInNudgeProps> = ({ feature, description, onSignedIn }) => {
  const [showForm, setShowForm] = useState(false);

  const handleSuccess = () => {
    const user = authService.getUser();
    if (user) {
      preferencesManager.setUserId(user.userId);
    }
    onSignedIn();
  };

  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 520,
        mx: 'auto',
        mt: { xs: 3, sm: 5 },
        mb: 4,
        p: { xs: 3, sm: 4 },
        textAlign: 'center',
        border: 1,
        borderColor: 'divider',
        borderRadius: 3,
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          mx: 'auto',
          mb: 2,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
        }}
      >
        <LockOutlined color="primary" fontSize="large" />
      </Box>

      <Typography variant="h6" fontWeight={700} gutterBottom>
        Sign in to {feature}
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
        {description ?? 'Creating a free account only takes a moment, and everything you save will be waiting for you next time.'}
      </Typography>

      {!showForm && (
        <Button variant="contained" size="large" onClick={() => setShowForm(true)}>
          Sign in / Create free account
        </Button>
      )}

      <Collapse in={showForm} unmountOnExit>
        <AuthForm onSuccess={handleSuccess} />
      </Collapse>
    </Paper>
  );
};

export default SignInNudge;
